import React, { useState, useCallback, type JSX } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/hooks/useAppApi'
import { PageHeader } from '@/components/composites/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Server, User, Monitor, Copy, Check } from 'lucide-react'

interface AboutData {
  appName: string
  brand: string
  environment: string
  region: string
  version: string
  buildDate: string | null
  serverTime: string
  database?: { connected: boolean }
  ecsTaskId?: string
  clientIp?: string
}

// Exported so the support-bundle metadata block reads the same browser
// detection the About page shows, rather than a second copy that can drift.
export function getBrowserInfo(): { browser: string; os: string; screenResolution: string; language: string; timezone: string } {
  const ua = navigator.userAgent

  let browser = 'Unknown'
  if (ua.includes('Firefox/')) {
    browser = `Firefox ${ua.split('Firefox/')[1]?.split(' ')[0] ?? ''}`
  } else if (ua.includes('Edg/')) {
    browser = `Edge ${ua.split('Edg/')[1]?.split(' ')[0] ?? ''}`
  } else if (ua.includes('Chrome/') && !ua.includes('Edg/')) {
    browser = `Chrome ${ua.split('Chrome/')[1]?.split(' ')[0] ?? ''}`
  } else if (ua.includes('Safari/') && !ua.includes('Chrome/')) {
    browser = `Safari ${ua.split('Version/')[1]?.split(' ')[0] ?? ''}`
  }

  let os = 'Unknown'
  if (ua.includes('Windows NT')) os = 'Windows'
  else if (ua.includes('Mac OS X')) os = 'macOS'
  else if (ua.includes('Linux')) os = 'Linux'
  else if (ua.includes('Android')) os = 'Android'
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS'

  return {
    browser,
    os,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }
}

function buildDiagnosticText(about: AboutData | undefined, user: { id?: string; name?: string; email?: string; scopes?: string[] } | null, browserInfo: ReturnType<typeof getBrowserInfo>): string {
  const lines: string[] = ['=== Diagnostic Info ===', '']

  if (about) {
    lines.push('-- App --')
    lines.push(`App: ${about.appName}`)
    lines.push(`Brand: ${about.brand}`)
    lines.push(`Version: ${about.version}`)
    if (about.buildDate) lines.push(`Build Date: ${about.buildDate}`)
    lines.push(`Environment: ${about.environment}`)
    lines.push(`Region: ${about.region}`)
    lines.push(`Server Time: ${about.serverTime}`)
    if (about.database) lines.push(`Database: ${about.database.connected ? 'Connected' : 'Disconnected'}`)
    if (about.ecsTaskId) lines.push(`Task ID: ${about.ecsTaskId}`)
    if (about.clientIp) lines.push(`Client IP: ${about.clientIp}`)
    lines.push('')
  }

  lines.push('-- User --')
  lines.push(`Name: ${user?.name ?? 'N/A'}`)
  lines.push(`Email: ${user?.email ?? 'N/A'}`)
  if (user?.id) lines.push(`Session ID: ${user.id}`)
  lines.push(`Scopes: ${user?.scopes?.join(', ') || 'N/A'}`)
  lines.push('')

  lines.push('-- Browser --')
  lines.push(`Browser: ${browserInfo.browser}`)
  lines.push(`OS: ${browserInfo.os}`)
  lines.push(`Screen: ${browserInfo.screenResolution}`)
  lines.push(`Language: ${browserInfo.language}`)
  lines.push(`Timezone: ${browserInfo.timezone}`)

  return lines.join('\n')
}

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }): JSX.Element => (
  <div className="flex justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-right">{value}</span>
  </div>
)

export const AboutPage = (): JSX.Element => {
  const api = useApi()
  const { user } = api
  const [copied, setCopied] = useState(false)

  const { data: about, isLoading } = useQuery<AboutData>({
    queryKey: ['about'],
    queryFn: (): Promise<AboutData> => api.get('/about') as Promise<AboutData>,
  })

  const browserInfo = getBrowserInfo()

  const handleCopy = useCallback(async (): Promise<void> => {
    const text = buildDiagnosticText(about, user, browserInfo)
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout((): void => setCopied(false), 2000)
  }, [about, user, browserInfo])

  if (isLoading) {
    return <div className="p-6 text-muted-foreground">Loading...</div>
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <PageHeader
        title="About"
        description="Application information and diagnostics."
        actions={
          <Button variant="outline" size="sm" onClick={handleCopy}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied' : 'Copy Diagnostic Info'}
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Server className="h-4 w-4" /> App Info
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <InfoRow label="App Name" value={about?.appName ?? '-'} />
          <InfoRow label="Brand" value={about?.brand ?? '-'} />
          <InfoRow label="Version" value={<span className="font-mono text-xs">{about?.version ?? '-'}</span>} />
          {about?.buildDate && <InfoRow label="Build Date" value={new Date(about.buildDate).toLocaleString()} />}
          <InfoRow label="Environment" value={<Badge variant="outline">{about?.environment ?? '-'}</Badge>} />
          <InfoRow label="Region" value={about?.region ?? '-'} />
          <InfoRow label="Server Time" value={about?.serverTime ? new Date(about.serverTime).toLocaleString() : '-'} />
          {about?.database && (
            <InfoRow
              label="Database"
              value={
                <Badge variant={about.database.connected ? 'default' : 'destructive'}>
                  {about.database.connected ? 'Connected' : 'Disconnected'}
                </Badge>
              }
            />
          )}
          {about?.ecsTaskId && <InfoRow label="Task ID" value={<span className="font-mono text-xs">{about.ecsTaskId}</span>} />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <User className="h-4 w-4" /> User Info
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <InfoRow label="Name" value={user?.name ?? '-'} />
          <InfoRow label="Email" value={user?.email ?? '-'} />
          {user?.id && <InfoRow label="Session ID" value={<span className="font-mono text-xs">{user.id}</span>} />}
          {about?.clientIp && <InfoRow label="IP Address" value={<span className="font-mono text-xs">{about.clientIp}</span>} />}
          {user?.scopes && user.scopes.length > 0 && (
            <div className="pt-1">
              <span className="text-muted-foreground">Scopes</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {user.scopes.map((s: string): JSX.Element => (
                  <Badge key={s} variant="outline" className="text-xs font-mono">{s}</Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Monitor className="h-4 w-4" /> Browser Info
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <InfoRow label="Browser" value={browserInfo.browser} />
          <InfoRow label="OS" value={browserInfo.os} />
          <InfoRow label="Screen Resolution" value={browserInfo.screenResolution} />
          <InfoRow label="Language" value={browserInfo.language} />
          <InfoRow label="Timezone" value={browserInfo.timezone} />
        </CardContent>
      </Card>
    </div>
  )
}
